import config from '../config';
import selectedEnvironment from '../env';
const API_URL = `${config[selectedEnvironment].API_URL}`;

const authHeaders = (auth, json = false) => ({
    Authorization: 'Bearer ' + auth,
    ...(json ? { 'Content-Type': 'application/json' } : {}),
});

// Returns whether the client has Finsite credential, linked cardIds, and accountantId.
const GetEligibility = async (CaseId, auth) => {
    const response = await fetch(`${API_URL}/finsite/eligibility/${encodeURIComponent(CaseId)}`, {
        method: 'GET',
        headers: authHeaders(auth),
    });

    return response;
};

// Manual, scoped fetch for a single client bank account (resolved by CaseId + CardId on the server).
const SyncCaseAccount = async (
    CaseId,
    CardId,
    FromDate,
    ToDate,
    auth,
    AttachDocumentsForExisting = true,
    ForceRefreshDocuments = false,
) => {
    const response = await fetch(`${API_URL}/finsite/sync-account`, {
        method: 'POST',
        headers: authHeaders(auth, true),
        body: JSON.stringify({
            CaseId,
            CardId,
            FromDate,
            ToDate,
            AttachDocumentsForExisting,
            ForceRefreshDocuments,
        }),
    });

    return response;
};

const GetCompanies = async (accountantId, auth) => {
    const response = await fetch(`${API_URL}/finsite/companies/${encodeURIComponent(accountantId)}`, {
        method: 'GET',
        headers: authHeaders(auth),
    });
    return response;
};

const GetAccounts = async (accountantId, auth) => {
    const response = await fetch(`${API_URL}/finsite/accounts/${encodeURIComponent(accountantId)}`, {
        method: 'GET',
        headers: authHeaders(auth),
    });
    return response;
};

const GetBookingAccounts = async (accountantId, auth) => {
    const response = await fetch(`${API_URL}/finsite/booking-accounts/${encodeURIComponent(accountantId)}`, {
        method: 'GET',
        headers: authHeaders(auth),
    });
    return response;
};

const CreateMapping = async (payload, auth) => {
    const response = await fetch(`${API_URL}/finsite/mappings`, {
        method: 'POST',
        headers: authHeaders(auth, true),
        body: JSON.stringify(payload),
    });
    return response;
};

const DeleteMapping = async (id, auth) => {
    const response = await fetch(`${API_URL}/finsite/mappings/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: authHeaders(auth),
    });
    return response;
};

// Live linked/not-linked summary for the accountant's own Finsite companies (= clients).
const GetAccountantSummary = async (accountantId, auth) => {
    const response = await fetch(`${API_URL}/finsite/accountant-summary/${encodeURIComponent(accountantId)}`, {
        method: 'GET',
        headers: authHeaders(auth),
    });
    return response;
};

const GetMappings = async (accountantId, auth) => {
    const response = await fetch(`${API_URL}/finsite/mappings/${encodeURIComponent(accountantId)}`, {
        method: 'GET',
        headers: authHeaders(auth),
    });
    return response;
};

const GetClients = async (accountantId, auth) => {
    const response = await fetch(`${API_URL}/finsite/clients/${encodeURIComponent(accountantId)}`, {
        method: 'GET',
        headers: authHeaders(auth),
    });
    return response;
};

const GetTBanks = async (caseId, auth) => {
    const response = await fetch(`${API_URL}/finsite/tbanks/${encodeURIComponent(caseId)}`, {
        method: 'GET',
        headers: authHeaders(auth),
    });
    return response;
};

// Auto-link every Finsite booking account of the accountant to matching bank accounts.
const AutoLink = async (accountantId, auth) => {
    const response = await fetch(`${API_URL}/finsite/auto-link`, {
        method: 'POST',
        headers: authHeaders(auth, true),
        body: JSON.stringify({ accountantId }),
    });
    return response;
};

const SyncAccountant = async (accountantId, FromDate, ToDate, auth) => {
    const response = await fetch(`${API_URL}/finsite/sync`, {
        method: 'POST',
        headers: authHeaders(auth, true),
        body: JSON.stringify({ accountantId, FromDate, ToDate }),
    });
    return response;
};

const AutoLinkClient = async (accountantId, caseId, auth) => {
    const response = await fetch(`${API_URL}/finsite/auto-link-client`, {
        method: 'POST',
        headers: authHeaders(auth, true),
        body: JSON.stringify({ accountantId, caseId }),
    });
    return response;
};

// Preview auto-link suggestions for one client (no persist).
const GetAutoLinkClientSuggestions = async (accountantId, caseId, auth) => {
    const response = await fetch(
        `${API_URL}/finsite/auto-link-suggestions/${encodeURIComponent(accountantId)}/${encodeURIComponent(caseId)}`,
        {
            method: 'GET',
            headers: authHeaders(auth),
        }
    );
    return response;
};

// Preview new Finsite transactions for a client bank account (no persist).
const PreviewCaseAccount = async (CaseId, CardId, FromDate, ToDate, auth) => {
    const response = await fetch(`${API_URL}/finsite/preview-account`, {
        method: 'POST',
        headers: authHeaders(auth, true),
        body: JSON.stringify({ CaseId, CardId, FromDate, ToDate }),
    });
    return response;
};

// Live Finsite Balance / TotalCredit / TotalDebit for a linked CaseId + CardId.
const GetCaseAccountBalances = async (CaseId, CardId, FromDate, ToDate, auth) => {
    const params = new URLSearchParams();
    if (FromDate) params.set('fromDate', FromDate);
    if (ToDate) params.set('toDate', ToDate);
    const qs = params.toString();
    const response = await fetch(
        `${API_URL}/finsite/balances/${encodeURIComponent(CaseId)}/${encodeURIComponent(CardId)}${qs ? `?${qs}` : ''}`,
        {
            method: 'GET',
            headers: authHeaders(auth),
        }
    );
    return response;
};

// The calling accountant's latest Finsite signup request (status chip in BankPages).
const GetMySignupRequest = async (auth) => {
    const response = await fetch(`${API_URL}/finsite/signup-requests/my`, {
        method: 'GET',
        headers: authHeaders(auth),
    });
    return response;
};

// Creates a request to join the Finsite service (idempotent while a request is open).
const CreateSignupRequest = async (payload, auth) => {
    const response = await fetch(`${API_URL}/finsite/signup-requests`, {
        method: 'POST',
        headers: authHeaders(auth, true),
        body: JSON.stringify(payload || {}),
    });
    return response;
};

// Permanently block Finsite EntryIDs (or the Finsite EntryIDs behind given transaction rows)
// from being re-imported by the daily worker / manual fetch.
const SuppressEntries = async (payload, auth) => {
    const response = await fetch(`${API_URL}/finsite/suppress-entries`, {
        method: 'POST',
        headers: authHeaders(auth, true),
        body: JSON.stringify(payload || {}),
    });
    return response;
};

const FinsiteService = {
    GetEligibility,
    SyncCaseAccount,
    PreviewCaseAccount,
    GetCaseAccountBalances,
    GetCompanies,
    GetAccounts,
    GetBookingAccounts,
    CreateMapping,
    DeleteMapping,
    AutoLinkClient,
    GetAutoLinkClientSuggestions,
    GetMySignupRequest,
    CreateSignupRequest,
    GetAccountantSummary,
    GetMappings,
    GetClients,
    GetTBanks,
    AutoLink,
    SyncAccountant,
    SuppressEntries,
};

export default FinsiteService;
